import { memo, MouseEventHandler, useMemo } from 'react'
import isEqual from 'react-fast-compare'
import { useCurrentRefinements } from 'react-instantsearch'
import { AtIcon, FocusElement, focusElementClasses } from '@curran-catalog/curran-atomic-library'

import { ServerState } from 'pages/catalog/[[...slug]]'

const extractSearchTerms = ({ filters }: { filters?: string }): string[] => {
  const regex = /(\w+)Slugs?:/g
  const matches = filters?.match(regex)
  if (matches) {
    return matches.map((match) => {
      const replaceSlugs = match.replace(/Slugs?:/, '')

      switch (replaceSlugs) {
        // Furniture cases
        case 'material':
          return 'materials'
        case 'vendor':
          return 'vendorName'

        // Flooring cases
        case 'pattern':
          return 'patterns'
        case 'type':
          return 'types'
        case 'usage':
          return 'usages'
        default:
          return replaceSlugs
      }
    })
  }
  return []
}

export interface ExpandablePanelProps {
  children: React.ReactNode
  className?: string
  header?: React.ReactNode | string
  footer?: string
  attributes?: string[]
  isOpened?: boolean
  hidden?: boolean
  onToggle?: MouseEventHandler
}

const ExpandablePanelComponent = ({
  children,
  header,
  attributes = [],
  isOpened = false,
  hidden = false,
  serverFilterFacets,
  onToggle,
}: ExpandablePanelProps & Pick<ServerState, 'serverFilterFacets'>) => {
  const { items } = useCurrentRefinements({ includedAttributes: attributes })

  const attributesByConfigure = useMemo(
    () => extractSearchTerms({ filters: serverFilterFacets ?? '' }),
    [serverFilterFacets],
  )

  const hasAttributesByConfigure = useMemo(
    () => attributesByConfigure.some((attr) => attr === attributes[0]),
    [attributes, attributesByConfigure],
  )

  return (
    <div className={`flex flex-col ${hidden || hasAttributesByConfigure ? 'opacity-0 hidden' : ''}`}>
      <button
        type="button"
        className={`w-full ${focusElementClasses[FocusElement.FOCUS_VISIBLE]}`}
        aria-expanded={isOpened}
        onClick={(e) => {
          if (typeof onToggle === 'function') {
            onToggle(e)
          }
        }}
      >
        <div className="bg-subdued flex items-center justify-between py-4 px-3">
          <div className="flex flex-row gap-x-2 items-center text-secondary text-lg font-medium">
            <span className="text-left">
              {header || attributes[0]}
              {items.length > 0 ? <span className="pl-3">{`(${items[0].refinements.length})`}</span> : null}
            </span>
          </div>
          <AtIcon type={isOpened ? 'minus' : 'plus'} color="secondary" />
        </div>
      </button>

      <div className={`${isOpened ? 'scale-y-100 h-max' : 'h-0 scale-y-0'}`}>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}

export const ExpandablePanel = memo(ExpandablePanelComponent, isEqual)
